import { Fragment, useCallback, useEffect, useState } from 'react';

import { Flex, Select, Text } from '@raystack/apsara';
import * as _ from 'lodash';

import * as Skeleton from '@src/components/PixxelUIKit/Skeleton';
import { For, Show } from '@src/hoc';

import { CategoricalGraph } from './CategoricalGraph';
import { CategoricalTable } from './CategoricalTable';

export type childClass = {
  [key: string]: string;
};

export type parentClasses = {
  [key: string]: childClass[];
};

type reducedChildClasses = {
  [key: string]: string;
};

interface CategoricalViewProps {
  isLoading: boolean;
  data: parentClasses;
  setChildClasses: (classes: reducedChildClasses) => void;
}

export const CategoricalView = ({
  isLoading,
  data,
  setChildClasses
}: CategoricalViewProps) => {
  const [activeParentClass, setActiveParentClass] = useState<string>('');

  //   handle activeParentClass change for both regular and split view
  const handleActiveParentClass = useCallback(
    (value: string) => {
      setActiveParentClass(value);
      if (!_.isEmpty(data)) {
        const childClasses: childClass[] = data[value];

        const reducedChildClasses: reducedChildClasses = childClasses.reduce(
          (acc, value) => {
            if (value['colors'] !== '')
              acc[value['Class index']] = value['colors'];

            return acc;
          },
          {}
        );
        setChildClasses(reducedChildClasses);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  //   select default class
  useEffect(() => {
    const defaultParent = Object.keys(data)[0] || undefined;
    if (defaultParent) handleActiveParentClass(defaultParent);
  }, []);

  return (
    <Fragment>
      <Show when={isLoading}>
        <Skeleton.TileList />
      </Show>
      <Show when={!isLoading}>
        <Flex
          direction='column'
          gap='small'
          align='start'
          style={{ width: '100%' }}
        >
          <Text size={4} weight={500}>
            From Class (Reference Asset)
          </Text>
          <Select
            value={activeParentClass}
            onValueChange={handleActiveParentClass}
          >
            <Select.Trigger style={{ width: '100%' }}>
              <Select.Value placeholder='Select a class' />
            </Select.Trigger>
            <Select.Content>
              <For each={Object.keys(data)} fallback={<></>}>
                {parentClassName => (
                  <Select.Item key={parentClassName} value={parentClassName}>
                    {parentClassName}
                  </Select.Item>
                )}
              </For>
            </Select.Content>
          </Select>
        </Flex>
        <Show when={activeParentClass !== ''}>
          <CategoricalGraph parentClass={activeParentClass} data={data} />
          <CategoricalTable parentClass={activeParentClass} data={data} />
        </Show>
      </Show>
    </Fragment>
  );
};
